import React, {FormEvent, useRef, useState} from 'react';
import {observer} from 'mobx-react-lite';
import intl from "react-intl-universal";


import {useAppState, useRootStore} from "@stores/AppStateContext";
import {useAuthServices} from "../../../../../platform/hooks/useServiceCombinations";
import {LogoIcon} from "../../../../components/icons/LogoIcon";
import {Modal} from "../../components/Modal";
import {MaterialUiInput} from "../../../../components/form/material-ui-input/MaterialUiInput";
import {Button} from "../../../../components/Button"
import {getInviteInfoFromLink} from "../../../../../actions/invite";
import {signUp} from "../common/signUp";
import {autoLogin} from "../common/autoLogin";
import {ModalBase} from "../../components/ModalBase";


import "./SignUpByLink.css";


interface FormErrors {
    email?: string | React.ReactElement;
    password?: string;
    password_confirm?: string;
}

interface FormData {
    firstName: { value: string };
    lastName: { value: string };
    email: { value: string };
    password: { value: string };
    password_confirm: { value: string };
}


export const SignUpByLink = observer(() => {
    const appState = useAppState();
    const rootStore = appState.rootStore;
    const authServices = useAuthServices();
    const [errors, setErrors] = useState<FormErrors>({});
    const isSubmittingRef = useRef(false);

    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showNewPasswordConfirm, setShowNewPasswordConfirm] = useState(false);

    const [isLoading, setIsLoading] = useState(false);

    const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (isSubmittingRef.current) {
            return;
        }
        isSubmittingRef.current = true;
        const {email, password, password_confirm, firstName, lastName} = event.target as typeof event.target & FormData;
        if (password_confirm.value !== password.value) {
            setErrors({password_confirm: intl.get('sign_up.confirm_pwd_not_match').d("The passwords you entered don't match.")})
            isSubmittingRef.current = false;
            return;
        } else {
            setErrors({});
        }
        setIsLoading(true);
        const inviteInfo = getInviteInfoFromLink();

        // ts:any because SignUpInput.options has errors in typescript declarations
        const signUpParams: any = {
            username: email.value,
            password: password.value,
            options: {
                validationData: inviteInfo,
            },
        };
        if (inviteInfo !== null) {
            signUpParams.options!.clientMetadata = inviteInfo;
        }

        const successfulSignup = await signUp(
            authServices.authService,
            signUpParams as any,
            setErrors,
            {appState},
        );

        if (successfulSignup) {
            await autoLogin(appState, rootStore.view, signUpParams.username, signUpParams.password, {
                name: firstName.value,
                family_name: lastName.value,
            });
        }
        isSubmittingRef.current = false;
        setIsLoading(false);
    };

    const terms_link = `<a href="/terms-and-conditions" class="TextLink" target="_blank">${intl.get('login.terms_link').d('Terms of Use')}</a>`;
    const privacy_link = `<a href="/privacy-policy" class="TextLink" target="_blank">${intl.get('login.privacy_link').d('Privacy Policy')}</a>`;
    const termsHtmlString = intl.getHTML('login.policy_statement', {
        terms_link,
        privacy_link,
    }).d(`<span>By proceeding to create your account and use Logycore, you are agreeing our ${terms_link} and ${privacy_link}.</span>`);

    return (
        <Modal
            open={true}
            disableEscapeKeyDown
        >
            <div className="SignUpByLink_modalBackground">
                <LogoIcon className="SignUpByLink_logo"/>
                <div className="SignUpByLink_Modal">
                    <ModalBase
                        open={true}
                        header={intl.get('sign-up-by-link.title').d('Create an account')}
                        maxWidth={512}
                        hideBackdrop={true}
                    >
                        <form onSubmit={handleSubmit}>
                            <div className="SignUpByLink__topText body-large-bold">
                                {intl.get('sign-up-by-link.text.top').d('Join your team on Logycore.')}
                            </div>
                            <div className="SignUpByLink__inputPart">
                                <div className={'SignUpByLink-Auth-fill-info-line  Auth-input'}>
                                    <MaterialUiInput
                                        className={'Auth-fill-info-line__item'}
                                        isRequired
                                        name="firstName"
                                        label={intl.get('fill_info.first_name').d('First Name')}
                                    />
                                    <MaterialUiInput
                                        className={'Auth-fill-info-line__item'}
                                        isRequired
                                        name="lastName"
                                        label={intl.get('fill_info.last_name').d('Last name')}
                                    />
                                </div>
                                <MaterialUiInput
                                    key="emailField"
                                    className={'Auth-input AuthModalBodyStart'}
                                    isRequired={true}
                                    name="email"
                                    label={intl.get('sign_up.email').d('Email')}
                                    fullWidth={true}
                                    trimSpace={true}
                                    error={errors.email}
                                />
                                <MaterialUiInput
                                    isRequired={true}
                                    className={'Auth-input'}
                                    name="password"
                                    type="password"
                                    autoComplete={"off"}
                                    showPassword={showNewPassword}
                                    setShowPassword={setShowNewPassword}
                                    label={intl.get('sign_up.password').d('Password')}
                                    trimSpace={true}
                                    fullWidth={true}
                                    error={errors.password}
                                />
                                <MaterialUiInput
                                    isRequired={true}
                                    className={'Auth-input'}
                                    name="password_confirm"
                                    type="password"
                                    autoComplete={"off"}
                                    showPassword={showNewPasswordConfirm}
                                    setShowPassword={setShowNewPasswordConfirm}
                                    trimSpace={true}
                                    label={intl.get('sign_up.password_confirm').d('Confirm password')}
                                    fullWidth={true}
                                    error={errors.password_confirm}
                                />
                                <div/>
                                <div className="SignUpByLink__buttonPart">
                                    <div className="SignUpByLink__buttonContinue">
                                        <Button
                                            style={'solid'}
                                            type="primary"
                                            htmlType="submit"
                                            fullWidth={true}
                                            className={'Auth-input'}
                                            text={intl.get('general.join_the_company').d('Join the company')}
                                            size={'lg'}
                                            loading={isLoading}
                                        />
                                    </div>
                                    <div className="SignUpByLink__bottomText body-medium">
                                        {termsHtmlString}
                                    </div>
                                </div>
                            </div>
                        </form>
                    </ModalBase>
                </div>
            </div>
        </Modal>
    );
})
